import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import { getSingleTranslation } from 'actions/general';
import { handleStorage, getRecent, getRealID, getLang, getIsMobile, getCookie, COOKIES, obtainVoucherPrices } from 'utils/helpers';
import { TARGETS_TRANSLATIONS, SOCKS_TYPE } from 'utils/constants/products';

import { parseDetailEcommerce, parseImpressionsProductsEcommerce } from 'utils/gtmUtils';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { addLazyLoadIntersectorSlider } from 'utils/lazyload';

import { useUser } from 'context/user';
import { useI18n } from 'context/i18n';
import { useSunnei } from 'context/product-sheet';
import { useIsMounted, useLocale } from 'hooks';
import { availableLookSkus } from 'components/shop-look';
import { getDomainValue } from 'utils/domain';
import type { ProductSheet } from 'types/product';

import styles from 'styles/ProductSheet.module.css';

const ProductPurchaseCol = dynamic(() => import('components/productPurchaseCol').then((mod) => mod.ProductPurchaseCol));
const LabPurchaseCol = dynamic(() => import('components/pdp/lab-purchase-col').then((mod) => mod.LabPurchaseCol));
const ProductsRow = dynamic(() => import('components/ProductsRow').then((mod) => mod.ProductsRow));
const Product = dynamic(() => import('components/product').then((mod) => mod.Product as any));
const ProductReviews = dynamic(() => import('components/productReviews'));
const BetterProduct = dynamic(() => import('components/betterProduct'));
const PdpDynamicComponent = getDomainValue({
  // @ts-ignore
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
  camper: dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
});
const Desktop3D = dynamic(() => import('components/viewer-3D/desktop3D').then((mod) => mod.Desktop3D));
const SunneiPicker = dynamic(() => import('components/sunnei/sunneiPicker').then((mod) => mod.SunneiPicker));
const ShopLook = dynamic(() => import('components/shop-look').then((mod) => mod.ShopLook));
const SeoContent = dynamic(() => import('components/seo-content').then((mod) => mod.SeoContent));
const PdpImageCarousel = dynamic(() => import('components/pdp/pdp-image-carousel').then((mod) => mod.PdpImageCarousel));
const ImagesGrid = dynamic(() => import('components/imagesGrid').then((mod) => mod.ImagesGrid));

const slots = {
  products: ProductsRow,
  product: Product,
};

type ProductSheetDefaultProps = {
  productSheet: ProductSheet;
  market: string;
  pdpProps?: any;
  seoContent?: any;
  recommendedProducts?: any;
};

export function ProductSheetDefault({ productSheet, market, pdpProps = null, seoContent = null, recommendedProducts = null }: ProductSheetDefaultProps) {
  const { t, profileData, giftCardList, physicalGiftCardList } = useI18n();
  const { contextFilters, activatedPrevouchers, handleActivatePrevoucher, initialAppliedVoucher } = useUser();
  const { locale } = useLocale();
  const router = useRouter();
  const isMounted = useIsMounted();
  const { hasSunneiConfig } = useSunnei();
  const { query } = router;
  const reviewsRef = useRef<HTMLDivElement | null>(null);
  const intervalID = useRef<ReturnType<typeof setInterval> | null>(null);
  const { target, product, size } = query;
  const averageRate = productSheet.rate ? Math.round(productSheet.rate * 10) / 10 : null;

  const [recentProducts, setRecentProducts] = useState([]);

  const [isFirst, setIsFirst] = useState(true);
  const [isMobileAfterSSR, setIsMobileAfterSSR] = useState(false);
  const [recalculating, setRecalculatingPrices] = useState(false);
  const [voucherApplied, setVoucherApplied] = useState(initialAppliedVoucher as boolean);
  const [currentProductSheet, setCurrentProductSheet] = useState(JSON.parse(JSON.stringify(productSheet)));
  const [voucherPriceText, setVoucherPriceText] = useState(null);
  const [usrhashValue, setUsrHashValue] = useState<string | null>(null);

  const isMobile = getIsMobile('767.5px');

  const isGiftCard = giftCardList.includes(productSheet.code) || physicalGiftCardList.includes(productSheet.code);
  const isSock = productSheet.sizeType === SOCKS_TYPE;
  const isCamperlab = productSheet.camperlab;

  const lang = getLang(locale);

  const hasReviews = !productSheet.emptyReviews && productSheet.code.substr(-1) !== 'Q';

  const targetRealID = getRealID('target', target);
  const familyRealID = getRealID('product', product);

  const handleVouchers = () => {
    const voucherPriceInfo = obtainVoucherPrices(currentProductSheet, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher, true, true);

    if (voucherPriceInfo !== null) {
      // siempre tendrá info si no es null
      const { message, info } = voucherPriceInfo;

      if (
        info.current !== currentProductSheet?.prices?.current ||
        info.discount !== currentProductSheet?.prices?.discount ||
        info.previous !== currentProductSheet?.prices?.previous
      ) {
        if (message) {
          // si tiene mensaje no debería pisar prices
          setVoucherPriceText(message);
        } else {
          setVoucherApplied(true);
          let newSizesPrice = null;

          if (productSheet?.prices?.pricePerSize === true) {
            newSizesPrice = productSheet?.sizes?.map((item: any) => {
              const voucherPriceSize = obtainVoucherPrices(item, t, locale, contextFilters, activatedPrevouchers, handleActivatePrevoucher);

              if (voucherPriceSize?.info) {
                return { ...item, price: voucherPriceSize.info };
              }

              return item;
            });
          }

          setCurrentProductSheet({
            ...productSheet,
            prices: info,
            sizes: newSizesPrice !== null ? newSizesPrice : productSheet?.sizes,
          });
          setVoucherPriceText(null);
        }
      }
    }
  };

  useEffect(() => {
    const cookieWatcher = () => {
      const voucher = getCookie(COOKIES.BONDS);
      const prevoucher = getCookie(COOKIES.PREVOUCHER);
      const usrhash = getCookie(COOKIES.SELLIGENT_USRHASH);

      if (voucher || prevoucher || (usrhash && usrhash !== usrhashValue)) {
        setUsrHashValue(usrhash);
        handleVouchers();
        if (intervalID.current !== null) {
          clearInterval(intervalID.current);
        }
      }
    };

    if (productSheet?.notifyMeType !== 'OUT') {
      setRecalculatingPrices(true);
      handleVouchers();
      setTimeout(() => {
        setRecalculatingPrices(false);
        intervalID.current = setInterval(cookieWatcher, 1000);
      });

      return () => {
        if (intervalID.current !== null) {
          clearInterval(intervalID.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    setIsMobileAfterSSR(isMobile);
  }, [isMobile]);

  useEffect(() => {
    const handleRecommended = async () => {
      if (getCookie(COOKIES.APP_IPAD) === 'true') return;
      let { [lang]: recentItems } = getRecent();
      const { code, brand, prices, imagesRecently, category, target: targetCode } = productSheet;
      const location = window.location.href.split(`/${locale}/`);
      const url = `${location[0]}/${locale}/${location[1]}`;
      const recentlySeenItem = {
        ...productSheet,
        subtitleH1: productSheet.codeDescription || '',
        colors: productSheet.brandProducts || [],
        id: code,
        prices,
        images: imagesRecently,
        name: brand,
        family: product,
        category,
        target: t(...(TARGETS_TRANSLATIONS[(targetCode as keyof typeof TARGETS_TRANSLATIONS) ?? 'M'] as [string, string])),
        url,
      };

      let push = true;

      if (recentItems === undefined) {
        recentItems = [];
      } else {
        const seenIDs = recentItems.map((item: any) => item.id);
        push = !seenIDs.includes(code);
      }

      if (push) {
        recentItems.unshift(recentlySeenItem);
      }

      const itemsToSet = recentItems.filter((item: any) => item.id !== code);
      setRecentProducts(itemsToSet);
      handleStorage('setItem', 'recentItems', JSON.stringify({ [lang]: recentItems }));

      if (recommendedProducts !== null) {
        let recommendedItems: any = [];

        try {
          if (!isMounted()) return;

          if (isFirst) {
            recommendedItems = {
              products: recommendedProducts.recommendeds,
              listName: recommendedProducts.listName,
              vmtoolConfig: recommendedProducts.vmtoolConfig,
            };
            setIsFirst(false);
          }
        } catch (error) {
          console.error(error);
        }

        const currency = profileData?.currencySap || '';
        const recentlyViewedKey = await getSingleTranslation('en_ES', 'ficha.producto', 'recently.browsed');
        if (!isMounted()) return;
        addLazyLoadIntersectorSlider(
          {
            recently_viewed: { products: itemsToSet, listName: recentlyViewedKey, vmtoolConfig: 'browsing-history' },
            you_may_also_be_interested: recommendedItems,
          },
          currency,
        );
      }
    };

    handleRecommended();
  }, [recommendedProducts]);

  const scrollToReviews = () => {
    dataLayerHandleEvent({
      event: 'item_reviews',
      event_category: 'product detail',
      event_detail_1: 'see reviews',
      event_detail_2: '',
    });

    reviewsRef.current?.scrollIntoView();
  };

  const { code } = productSheet;

  let mayBeInterestedTitle = t('ficha.producto', 'tambien.puede.interesarte');

  if (productSheet.isSunnei) {
    mayBeInterestedTitle = t('ficha.producto', 'completa.sunnei');
  }

  return (
    <div
      id="productSheetWrapper"
      className={`${styles.productSheet} lab:max-md:-mt-44 lab:max-md:[@media(min-height:700px)]:-mt-36 lab:max-md:[@media(min-height:780px)]:-mt-16`}
    >
      <div className="flex flex-col md:flex-row lab:lg:gap-12 lab:xl:gap-32">
        {/* <Col xs={24} md={getDomainValue({ camper: 14, nnormal: 14, camperlab: 24 })} lg={16}> */}
        <div className="normal:[@media(min-width:992px)]:basis-8/12 basis-full md:basis-7/12 md:lab:basis-full lg:basis-8/12 camper:[@media(min-width:992px)]:basis-8/12">
          {hasSunneiConfig ?
            <div className="max-md:hidden">
              <SunneiPicker />
            </div>
          : null}
          <div
            className={getDomainValue({
              camper: `${styles.imagesRow} ${productSheet.newShooting === true ? '' : styles.oldShooting}`,
              nnormal: `${styles.imagesRow} ${productSheet.newShooting === true ? '' : styles.oldShooting}`,
              camperlab: 'mx-2.5',
            })}
          >
            {productSheet.viewer3d === true ?
              <Desktop3D code={productSheet.code} />
            : null}
            {availableLookSkus.includes(code) ?
              <ShopLook primarySku={code} />
            : null}
            {getDomainValue({
              camper: <ImagesGrid />,
              camperlab: <PdpImageCarousel />,
              nnormal: <ImagesGrid />,
            })}
          </div>
        </div>
        <div className="flex-shrink-0 basis-full md:basis-5/12 lab:lg:w-[clamp(264px,30vw,367px)] lab:lg:basis-[auto] nnormal:[@media(min-width:992px)]:basis-4/12 camper:[@media(min-width:992px)]:basis-4/12">
          {getDomainValue({
            camper: (
              <ProductPurchaseCol
                averageRate={averageRate}
                hasReviews={hasReviews}
                locale={locale}
                market={market}
                productSheet={currentProductSheet}
                isSock={isSock}
                isGiftCard={isGiftCard}
                onClickReviews={scrollToReviews}
                recalculating={recalculating}
                voucherApplied={voucherApplied}
                voucherPriceText={voucherPriceText ?? null}
                usrhashValue={usrhashValue as TODO}
              />
            ),
            nnormal: (
              <ProductPurchaseCol
                averageRate={averageRate}
                hasReviews={hasReviews}
                locale={locale}
                market={market}
                productSheet={currentProductSheet}
                isSock={isSock}
                isGiftCard={isGiftCard}
                onClickReviews={scrollToReviews}
                recalculating={recalculating}
                voucherApplied={voucherApplied}
                voucherPriceText={voucherPriceText ?? null}
                usrhashValue={usrhashValue as TODO}
              />
            ),
            camperlab: (
              <LabPurchaseCol
                recalculating={recalculating}
                voucherApplied={voucherApplied}
                voucherPriceText={voucherPriceText as TODO}
                usrhashValue={usrhashValue as TODO}
                currentProductSheet={currentProductSheet}
              />
            ),
          })}
        </div>
      </div>
      {productSheet.betterProduct && productSheet.betterProduct.tiers && productSheet.betterProduct.tiers.length > 0 && (
        <BetterProduct title={t('ficha.producto', 'better.product')} tiers={productSheet.betterProduct.tiers} />
      )}
      {PdpDynamicComponent !== null && pdpProps !== null && pdpProps?.top !== null && (
        <PdpDynamicComponent market={pdpProps.market} data={pdpProps.top} slots={slots} />
      )}
      {recommendedProducts !== null && (
        <div id="recomendador">
          <ProductsRow
            title={mayBeInterestedTitle}
            locale={locale}
            source={recommendedProducts?.recommendeds}
            currentList="You_may_also_be_interested"
            slider={true}
            camperlab={isCamperlab}
            withHover={!isMobileAfterSSR}
            fixedGrid={false}
            squareImg={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
          />
        </div>
      )}
      <ProductsRow
        title={t('ficha.producto', 'recently.browsed')}
        locale={locale}
        source={recentProducts}
        currentList="Recently_viewed"
        slider={true}
        camperlab={isCamperlab}
        withHover={!isMobileAfterSSR}
        fixedGrid={false}
        squareImg={getDomainValue({ camper: false, nnormal: false, camperlab: false })}
      />
      {PdpDynamicComponent !== null && pdpProps !== null && pdpProps?.bottom !== null && (
        <PdpDynamicComponent market={pdpProps.market} data={pdpProps.bottom} slots={slots} />
      )}
      {seoContent !== null && <SeoContent content={seoContent} camperlab={isCamperlab} />}
      {hasReviews && !productSheet.recycled && !productSheet.camperlab && (
        <ProductReviews
          locale={locale}
          productCode={productSheet.code}
          reviewStatistics={productSheet.reviewStatistics}
          firstReviews={productSheet.firstReviews}
          averageRate={averageRate ?? 0}
          reviewsRef={reviewsRef}
          line={productSheet.brand as TODO}
        />
      )}
    </div>
  );
}
